import { useApolloClient } from '@vue/apollo-composable';
import { addDays } from 'date-fns';

import type {
  OfferDetailsRoom,
  OfferDetailsRoomOffer,
  OfferDetailsRoomOfferTax,
  RoomDetailsOffersQuery,
  RoomOffers,
} from '~/domains/graphql';
import { RoomDetailsOffersDocument } from '~/domains/graphql';
import { useCurrenciesMap } from '~/domains/referential';
import { useUserStore } from '~/domains/user';
import type { PriceItem } from '~/helpers';
import {
  convertTo,
  extractDateFromApiString,
  getOfferPrice,
  getPrice,
} from '~/helpers';

import { useBookingStore } from '../../store';
import {
  isPromotionalOffer,
  isPublicOrLoyaltyOffer,
  publicOrLoyaltyOffers,
} from '../../helpers/isOffer.helper';

type QueryParameters = {
  offerCode?: string;
  adults?: number;
  childrenAges?: number[];
  hotelCode?: string;
  nights?: number;
};

export function useOffer(parameters: Ref<QueryParameters>) {
  const { client } = useApolloClient();

  const loading = ref<boolean>(false);
  const offerDetails = ref<OfferDetailsRoom>();

  const publicOffer = computed<OfferDetailsRoomOffer | undefined>(() => {
    if (!Array.isArray(offerDetails.value?.offers)) return;

    return publicOrLoyaltyOffers(
      (offerDetails.value?.offers ?? []) as OfferDetailsRoomOffer[],
    ).find(offer => offer?.categories?.some(isPublicOrLoyaltyOffer)) as
      | OfferDetailsRoomOffer
      | undefined;
  });

  const memberOffer = computed<OfferDetailsRoomOffer | undefined>(() => {
    if (!Array.isArray(offerDetails.value?.offers)) return;

    return offerDetails.value?.offers.find(offer =>
      offer?.categories?.some(isPromotionalOffer),
    ) as OfferDetailsRoomOffer | undefined;
  });

  const canFetchOffer = computed(() => {
    const { adults, nights, offerCode, hotelCode } = parameters.value;
    return Boolean(adults && nights && offerCode && hotelCode);
  });

  async function fetchOffer() {
    if (loading.value || !canFetchOffer.value) return;

    loading.value = true;

    try {
      const response = await client.query<RoomDetailsOffersQuery>({
        query: RoomDetailsOffersDocument,
        variables: parameters.value,
      });

      offerDetails.value = response.data.roomOfferDetails as OfferDetailsRoom;
    } finally {
      loading.value = false;
    }
  }

  watch(canFetchOffer, () => {
    fetchOffer();
  });

  watch(parameters, () => {
    fetchOffer();
  });

  onMounted(() => {
    fetchOffer();
  });

  return { loading, offerDetails, publicOffer, memberOffer };
}

type OfferToDrawerOffers = {
  offerDetails: Ref<OfferDetailsRoom | undefined>;
  publicOffer: Ref<OfferDetailsRoomOffer | undefined>;
  memberOffer?: Ref<OfferDetailsRoomOffer | undefined>;
};

type OfferToDrawerOptions = {
  areAllTaxesIncluded: Ref<boolean>;
};

export function useOfferToDrawer(
  { offerDetails, publicOffer, memberOffer }: OfferToDrawerOffers,
  { areAllTaxesIncluded }: OfferToDrawerOptions,
) {
  const userStore = useUserStore();
  const bookingStore = useBookingStore();
  const isLogged = computed(() => userStore.isLogged);
  const wantSignup = computed(() => bookingStore.wantSignup);
  const userCurrency = computed(() => bookingStore.currentCurrency);
  const { d } = useI18n();

  const currencies = useCurrenciesMap();

  const useMemberWhenAvailable = computed(
    () => isLogged.value || wantSignup.value,
  );

  const title = computed(() => offerDetails.value?.label);

  const subtitle = computed(() => {
    if (useMemberWhenAvailable.value && memberOffer?.value?.label)
      return memberOffer.value.label;

    return publicOffer.value?.label;
  });

  const description = computed(() => {
    if (useMemberWhenAvailable.value && memberOffer?.value?.description)
      return memberOffer.value?.description;

    return publicOffer.value?.description;
  });

  const policies = computed(() => {
    if (useMemberWhenAvailable.value && memberOffer?.value?.policies)
      return memberOffer?.value.policies;

    return publicOffer.value?.policies ?? [];
  });

  const memberOfferPrice = computed(() =>
    getOfferPrice(
      areAllTaxesIncluded.value,
      memberOffer?.value as RoomOffers | undefined,
    ),
  );
  const memberOfferCurrency = computed(
    () => memberOffer?.value?.pricing?.currency,
  );
  const memberPrice = useConvertToUserCurrency(
    memberOfferPrice,
    memberOfferCurrency,
    userCurrency,
  );

  const publicOfferPrice = computed(() =>
    getOfferPrice(
      areAllTaxesIncluded.value,
      publicOffer?.value as RoomOffers | undefined,
    ),
  );
  const publicOfferCurrency = computed(
    () => publicOffer.value?.pricing?.currency,
  );
  const publicPrice = useConvertToUserCurrency(
    publicOfferPrice,
    publicOfferCurrency,
    userCurrency,
  );

  const nightsDetails = computed(() => {
    return offerDetails.value?.detail?.map(item => {
      const rawStartDate = extractDateFromApiString(item?.date);
      const rawEndDate = addDays(rawStartDate, 1);

      const startDate = d(rawStartDate, 'bookingRateDate');
      const endDate = d(rawEndDate, 'bookingRateDate');

      const mealCode =
        memberOffer?.value?.mealPlan ?? publicOffer.value?.mealPlan;

      const nightDetails = {
        startDate,
        endDate,
        mealCode,
        price: 0,
      };

      if (item?.offers?.length === 1) {
        const itemCurrency = item.offers[0]?.pricing?.currency ?? '';
        const currency = currencies.value[itemCurrency];
        const userCurrency = currencies.value[bookingStore.currentCurrency];

        nightDetails.price = convertTo(
          getPrice(areAllTaxesIncluded.value, item.offers[0] as PriceItem),
          currency,
          userCurrency,
        );
      }

      return nightDetails;
    });
  });

  const mealPlanTitle = computed(() => {
    if (
      useMemberWhenAvailable.value &&
      memberOffer?.value?.mainMealPlanDescription
    )
      return memberOffer.value.mainMealPlanDescription;

    return publicOffer.value?.mainMealPlanDescription;
  });

  const mealPlanIncluded = computed(() => {
    const mealPlanStatus =
      memberOffer?.value?.mealPlan ?? publicOffer.value?.mealPlan ?? '';

    return ['BB', 'ALL', 'HB', 'FB'].includes(mealPlanStatus);
  });

  function filterTaxes(
    taxes: OfferDetailsRoomOfferTax[] | undefined,
    included: boolean,
  ) {
    if (areAllTaxesIncluded.value && included)
      return taxes?.map(tax => tax.label);
    else if (areAllTaxesIncluded.value && !included) return [];

    return taxes
      ?.filter(tax => (included ? tax.included : !tax.included))
      .map(tax => tax.label);
  }

  const taxesIncluded = computed(() => {
    if (useMemberWhenAvailable.value && Array.isArray(memberOffer?.value?.tax))
      return filterTaxes(
        memberOffer?.value?.tax as OfferDetailsRoomOfferTax[],
        true,
      );

    return filterTaxes(
      publicOffer?.value?.tax as OfferDetailsRoomOfferTax[],
      true,
    );
  });

  const taxesExcluded = computed(() => {
    if (useMemberWhenAvailable.value && Array.isArray(memberOffer?.value?.tax))
      return filterTaxes(
        memberOffer?.value?.tax as OfferDetailsRoomOfferTax[],
        false,
      );

    return filterTaxes(
      publicOffer?.value?.tax as OfferDetailsRoomOfferTax[],
      false,
    );
  });

  return {
    title, // Room label
    subtitle, // Rate label
    description, // Rate description
    policies, // Rate policies
    memberPrice,
    publicPrice,
    nightsDetails,
    mealPlanTitle,
    mealPlanIncluded,
    taxesIncluded,
    taxesExcluded,
  };
}
